import * as THREE from 'three';
import { VALVE_COLORS, type Coin, type CrateSnap, type Hazard, type MapDef, type Tuple3, type WorldObject } from '@kokun/shared';
import { canvasTexture, ownToon, toon } from '../core/materials';

const G = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(1, 1, 1, 20),
  sphere: new THREE.SphereGeometry(1, 16, 12),
  torus: new THREE.TorusGeometry(0.5, 0.08, 8, 24),
  coin: new THREE.CylinderGeometry(0.35, 0.35, 0.08, 20),
};

/** Célula de energía (también se usa al llevarla sobre la cabeza). */
export function makeCell(): THREE.Group {
  const g = new THREE.Group();
  const core = new THREE.Mesh(G.cyl, toon(0x4cc9f0, { emissive: 0x1a7fa0 }));
  core.scale.set(0.22, 0.6, 0.22);
  const capM = toon(0xadb5bd);
  const c1 = new THREE.Mesh(G.cyl, capM);
  c1.scale.set(0.27, 0.1, 0.27);
  c1.position.y = 0.33;
  const c2 = c1.clone();
  c2.position.y = -0.33;
  g.add(core, c1, c2);
  return g;
}

interface ItemView {
  group: THREE.Group;
  beam: THREE.Mesh;
  obj: Extract<WorldObject, { kind: 'item' }>;
}

export class ObjectsView {
  readonly root = new THREE.Group();
  private items = new Map<string, ItemView>();
  private valves = new Map<string, { wheel: THREE.Object3D; light: THREE.MeshToonMaterial }>();
  private plates = new Map<string, { pad: THREE.Mesh; mat: THREE.MeshToonMaterial }>();
  private lever!: { stick: THREE.Object3D; ring: THREE.Mesh; ringMat: THREE.MeshBasicMaterial };
  private displayCtx!: { ctx: CanvasRenderingContext2D; tex: THREE.CanvasTexture };
  private button!: { cap: THREE.Mesh; mat: THREE.MeshToonMaterial; readyAt: number };
  private crates = new Map<string, THREE.Mesh>();
  private hazards = new Map<string, THREE.Group>();
  private coins = new Map<string, THREE.Mesh>();
  private teleporters: THREE.Mesh[] = [];
  private coreMat: THREE.MeshToonMaterial;
  private coinMat = toon(0xffd60a, { emissive: 0x7a5c00 });
  /** Jugadores que llevan algo: id de objeto → id de jugador. */
  carriers = new Map<string, string>();
  objects = new Map<string, WorldObject>();
  displayColors: number[] = [];

  constructor(
    private map: MapDef,
    private serverNow: () => number,
  ) {
    this.coreMat = ownToon(0x3a86ff, 0x0b1d51);
    for (const d of map.interactables) {
      const [x, y, z] = d.pos;
      switch (d.kind) {
        case 'valve': {
          const g = new THREE.Group();
          g.position.set(x, y, z);
          const pipe = new THREE.Mesh(G.cyl, toon(0x6c757d));
          pipe.scale.set(0.15, 1.2, 0.15);
          pipe.position.y = 0.6;
          const wheel = new THREE.Group();
          wheel.position.y = 1.25;
          const rim = new THREE.Mesh(G.torus, toon(0xe63946));
          rim.rotation.x = Math.PI / 2;
          wheel.add(rim);
          for (let i = 0; i < 3; i++) {
            const sp = new THREE.Mesh(G.box, toon(0xe63946));
            sp.scale.set(0.9, 0.06, 0.06);
            sp.rotation.y = (i / 3) * Math.PI;
            wheel.add(sp);
          }
          const light = ownToon(VALVE_COLORS[0], VALVE_COLORS[0]);
          light.emissiveIntensity = 0.7;
          const bulb = new THREE.Mesh(G.sphere, light);
          bulb.scale.setScalar(0.2);
          bulb.position.y = 1.55;
          g.add(pipe, wheel, bulb);
          this.root.add(g);
          this.valves.set(d.id, { wheel, light });
          break;
        }
        case 'plate': {
          const mat = ownToon(0xff9f1c, 0x331a00);
          const pad = new THREE.Mesh(G.cyl, mat);
          pad.scale.set(d.radius, 0.12, d.radius);
          pad.position.set(x, 0.06, z);
          const rim = new THREE.Mesh(G.cyl, toon(0x495057));
          rim.scale.set(d.radius + 0.15, 0.06, d.radius + 0.15);
          rim.position.set(x, 0.03, z);
          this.root.add(rim, pad);
          this.plates.set(d.id, { pad, mat });
          break;
        }
        case 'lever': {
          const g = new THREE.Group();
          g.position.set(x, y, z);
          const base = new THREE.Mesh(G.box, toon(0x343a40));
          base.scale.set(0.8, 0.4, 0.5);
          base.position.y = 0.2;
          const stick = new THREE.Group();
          stick.position.y = 0.4;
          const bar = new THREE.Mesh(G.cyl, toon(0xadb5bd));
          bar.scale.set(0.06, 1, 0.06);
          bar.position.y = 0.5;
          const knob = new THREE.Mesh(G.sphere, toon(0xe63946));
          knob.scale.setScalar(0.15);
          knob.position.y = 1;
          stick.add(bar, knob);
          stick.rotation.z = 0.6;
          g.add(base, stick);
          this.root.add(g);
          const zone = map.zones.find((zz) => zz.kind === 'generatorRing')!;
          const ringMat = new THREE.MeshBasicMaterial({ color: 0xffd60a, transparent: true, opacity: 0.0 });
          const ring = new THREE.Mesh(new THREE.RingGeometry(zone.radius - 0.3, zone.radius, 64), ringMat);
          ring.rotation.x = -Math.PI / 2;
          ring.position.set(zone.pos[0], 0.35, zone.pos[2]);
          this.root.add(ring);
          this.lever = { stick, ring, ringMat };
          break;
        }
        case 'display': {
          const c = document.createElement('canvas');
          c.width = 512;
          c.height = 256;
          const tex = new THREE.CanvasTexture(c);
          tex.colorSpace = THREE.SRGBColorSpace;
          const frame = new THREE.Mesh(G.box, toon(0x212529));
          frame.scale.set(0.3, 3.4, 6.4);
          frame.position.set(x + 0.15, y, z);
          const screen = new THREE.Mesh(new THREE.PlaneGeometry(6, 3), new THREE.MeshBasicMaterial({ map: tex }));
          screen.position.set(x - 0.02, y, z);
          screen.rotation.y = d.rotY ?? 0;
          this.root.add(frame, screen);
          this.displayCtx = { ctx: c.getContext('2d')!, tex };
          this.drawDisplay([]);
          break;
        }
        case 'redButton': {
          const mat = ownToon(0xff1e3c, 0x550000);
          const cap = new THREE.Mesh(new THREE.SphereGeometry(0.4, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat);
          cap.position.set(x, y, z);
          this.root.add(cap);
          this.button = { cap, mat, readyAt: 0 };
          break;
        }
        case 'teleporter': {
          const m = new THREE.Mesh(new THREE.TorusGeometry(d.radius * 0.8, 0.12, 8, 32), toon(0x9d4edd, { emissive: 0x5a189a }));
          m.rotation.x = -Math.PI / 2;
          m.position.set(x, 0.12, z);
          const disc = new THREE.Mesh(new THREE.CircleGeometry(d.radius * 0.75, 32), new THREE.MeshBasicMaterial({ color: 0xc77dff, transparent: true, opacity: 0.5 }));
          disc.rotation.x = -Math.PI / 2;
          disc.position.set(x, 0.05, z);
          this.root.add(m, disc);
          this.teleporters.push(m);
          break;
        }
        case 'vanishSwitch': {
          const post = new THREE.Mesh(G.box, toon(0xffe066));
          post.scale.set(0.6, 1.1, 0.6);
          post.position.set(x, 0.55, z);
          const btn = new THREE.Mesh(G.cyl, toon(0x3a86ff, { emissive: 0x112266 }));
          btn.scale.set(0.2, 0.1, 0.2);
          btn.position.set(x, 1.15, z);
          this.root.add(post, btn);
          break;
        }
        case 'socket': {
          // Núcleo del generador: brilla más a medida que avanza la misión
          const glow = new THREE.Mesh(G.sphere, this.coreMat);
          glow.scale.setScalar(0.9);
          glow.position.set(0, 2.6, -22);
          this.root.add(glow);
          break;
        }
      }
    }
    for (const c of map.crates) {
      const m = new THREE.Mesh(
        G.box,
        new THREE.MeshToonMaterial({
          map: canvasTexture(64, 64, (ctx) => {
            ctx.fillStyle = '#c68b59';
            ctx.fillRect(0, 0, 64, 64);
            ctx.strokeStyle = '#7f5539';
            ctx.lineWidth = 6;
            ctx.strokeRect(3, 3, 58, 58);
            ctx.beginPath();
            ctx.moveTo(3, 3);
            ctx.lineTo(61, 61);
            ctx.stroke();
          }),
        }),
      );
      m.scale.setScalar(c.size);
      m.position.set(c.pos[0], c.pos[1] + c.size / 2, c.pos[2]);
      m.castShadow = m.receiveShadow = true;
      this.root.add(m);
      this.crates.set(c.id, m);
    }
  }

  private drawDisplay(colors: number[]) {
    const { ctx, tex } = this.displayCtx;
    ctx.fillStyle = '#0b132b';
    ctx.fillRect(0, 0, 512, 256);
    ctx.fillStyle = '#5bc0be';
    ctx.font = '700 30px "Baloo 2", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('CALIBRACIÓN DEL GENERADOR', 256, 42);
    if (!colors.length) {
      ctx.fillStyle = '#3a506b';
      ctx.font = '900 80px "Baloo 2", sans-serif';
      ctx.fillText('· · ·', 256, 170);
    } else {
      colors.forEach((c, i) => {
        ctx.fillStyle = `#${VALVE_COLORS[c].toString(16).padStart(6, '0')}`;
        ctx.beginPath();
        ctx.roundRect(46 + i * 150, 76, 120, 120, 20);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 22px sans-serif';
        ctx.fillText(['IZQ', 'TRAS', 'DER'][i] ?? String(i + 1), 106 + i * 150, 226);
      });
    }
    tex.needsUpdate = true;
  }

  applyObjects(list: WorldObject[], full = false) {
    if (full) {
      for (const id of [...this.items.keys()]) if (!list.some((o) => o.id === id)) this.removeItem(id);
      this.carriers.clear();
    }
    for (const o of list) {
      this.objects.set(o.id, o);
      switch (o.kind) {
        case 'item': {
          let v = this.items.get(o.id);
          if (!v) {
            const group = makeCell();
            const beam = new THREE.Mesh(G.cyl, new THREE.MeshBasicMaterial({ color: 0x4cc9f0, transparent: true, opacity: 0.25, depthWrite: false }));
            beam.scale.set(0.35, 30, 0.35);
            beam.position.y = 15;
            group.add(beam);
            this.root.add(group);
            v = { group, beam, obj: o };
            this.items.set(o.id, v);
          }
          v.obj = o;
          v.group.visible = o.state !== 'carried';
          v.beam.visible = o.state === 'idle';
          v.group.position.set(o.pos[0], o.pos[1] + 0.8, o.pos[2]);
          if (o.carrier) this.carriers.set(o.id, o.carrier);
          else this.carriers.delete(o.id);
          break;
        }
        case 'valve': {
          const v = this.valves.get(o.id);
          if (v) {
            v.light.color.setHex(VALVE_COLORS[o.color]);
            v.light.emissive.setHex(VALVE_COLORS[o.color]);
            v.wheel.userData.spin = 1;
          }
          break;
        }
        case 'plate': {
          const p = this.plates.get(o.id);
          if (p) {
            p.pad.position.y = o.pressed ? 0.02 : 0.06;
            p.mat.color.setHex(o.pressed ? 0x06d6a0 : 0xff9f1c);
            p.mat.emissive.setHex(o.pressed ? 0x03543f : 0x331a00);
          }
          break;
        }
        case 'lever':
          this.lever.stick.rotation.z = o.active ? -0.6 : 0.6;
          this.lever.ringMat.opacity = o.active ? 0.35 + o.progress * 0.5 : 0;
          this.coreMat.emissiveIntensity = 1 + o.progress * 3;
          break;
        case 'display':
          this.displayColors = o.colors;
          this.drawDisplay(o.colors);
          break;
        case 'button':
          this.button.readyAt = o.readyAt;
          break;
      }
    }
    const delivered = [...this.items.values()].filter((i) => i.obj.state === 'delivered').length;
    this.coreMat.emissive.setHex(delivered ? 0x4cc9f0 : 0x0b1d51);
    this.coreMat.emissiveIntensity = Math.max(this.coreMat.emissiveIntensity, delivered * 0.6);
  }

  private removeItem(id: string) {
    const v = this.items.get(id);
    if (v) this.root.remove(v.group);
    this.items.delete(id);
    this.objects.delete(id);
  }

  applyCrates(list: CrateSnap[]) {
    for (const c of list) {
      const m = this.crates.get(c.id);
      if (m) m.userData.target = c.p;
    }
  }

  applyHazards(list: Hazard[]) {
    for (const [id, g] of this.hazards) {
      if (!list.some((h) => h.id === id)) {
        this.root.remove(g);
        this.hazards.delete(id);
      }
    }
    for (const h of list) {
      if (this.hazards.has(h.id)) continue;
      const g = new THREE.Group();
      const puddle = new THREE.Mesh(new THREE.CircleGeometry(h.r, 28), new THREE.MeshBasicMaterial({ color: 0xbde0fe, transparent: true, opacity: 0.7, depthWrite: false }));
      puddle.rotation.x = -Math.PI / 2;
      puddle.position.y = 0.04;
      const peel = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.1, 6, 12, Math.PI), toon(0xffe066));
      peel.position.y = 0.15;
      peel.rotation.x = -Math.PI / 2;
      g.add(puddle, peel);
      for (let i = 0; i < 6; i++) {
        const b = new THREE.Mesh(G.sphere, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 }));
        b.scale.setScalar(0.08 + (i % 3) * 0.04);
        b.position.set(Math.cos(i) * h.r * 0.6, 0.15, Math.sin(i * 1.3) * h.r * 0.6);
        b.userData.bubble = i;
        g.add(b);
      }
      g.position.set(h.x, this.groundAt(h.x, h.z), h.z);
      this.root.add(g);
      this.hazards.set(h.id, g);
    }
  }

  groundAt: (x: number, z: number) => number = () => 0;

  setCoins(coins: Coin[]) {
    for (const m of this.coins.values()) this.root.remove(m);
    this.coins.clear();
    this.addCoins(coins);
  }
  addCoins(coins: Coin[]) {
    for (const c of coins) {
      const m = new THREE.Mesh(G.coin, this.coinMat);
      m.rotation.x = Math.PI / 2;
      m.position.set(c.x, c.y, c.z);
      m.castShadow = true;
      this.root.add(m);
      this.coins.set(c.id, m);
    }
  }
  removeCoins(ids: string[]): Tuple3[] {
    const out: Tuple3[] = [];
    for (const id of ids) {
      const m = this.coins.get(id);
      if (m) {
        out.push([m.position.x, m.position.y, m.position.z]);
        this.root.remove(m);
      }
      this.coins.delete(id);
    }
    return out;
  }

  /** Posiciones de interés para los indicadores de objetivo del HUD. */
  itemPositions(): Array<{ id: string; pos: THREE.Vector3; state: string }> {
    return [...this.items.values()].map((v) => ({ id: v.obj.id, pos: v.group.position, state: v.obj.state }));
  }

  update(dt: number, t: number) {
    for (const v of this.items.values()) {
      if (v.obj.state === 'idle') {
        v.group.rotation.y += dt * 1.5;
        v.group.position.y = v.obj.pos[1] + 0.8 + Math.sin(t * 2.5) * 0.15;
      }
    }
    for (const v of this.valves.values()) {
      if (v.wheel.userData.spin > 0) {
        v.wheel.rotation.y += dt * 12;
        v.wheel.userData.spin -= dt * 2.5;
      }
    }
    for (const m of this.crates.values()) {
      const p = m.userData.target as Tuple3 | undefined;
      if (p) {
        m.position.x += (p[0] - m.position.x) * Math.min(1, dt * 15);
        m.position.z += (p[2] - m.position.z) * Math.min(1, dt * 15);
      }
    }
    for (const c of this.coins.values()) {
      c.rotation.z += dt * 3;
      c.position.y = 0.8 + Math.sin(t * 3 + c.position.x) * 0.12;
    }
    for (const g of this.hazards.values())
      g.children.forEach((b) => {
        if (b.userData.bubble !== undefined) b.position.y = 0.15 + Math.abs(Math.sin(t * 2 + b.userData.bubble)) * 0.3;
      });
    for (const tp of this.teleporters) tp.rotation.z += dt * 2;
    const cooling = this.serverNow() < this.button.readyAt;
    this.button.mat.emissiveIntensity = cooling ? 0.1 : 0.6 + Math.sin(t * 5) * 0.4;
    this.button.mat.color.setHex(cooling ? 0x7a2a35 : 0xff1e3c);
    this.coreMat.emissiveIntensity = Math.max(0.6, this.coreMat.emissiveIntensity * 0.999);
    void this.map;
  }
}
